import React, { memo } from 'react';

interface SkeletonLoaderProps {
  variant?: 'card' | 'list' | 'table' | 'metrics';
  count?: number;
  height?: number | string;
  width?: number | string;
  className?: string;
}

// Base skeleton styles with CSS animation
const skeletonStyles = {
  skeleton: {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'skeleton-loading 1.5s infinite',
    borderRadius: '4px',
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  list: {
    padding: '0.75rem',
    borderBottom: '1px solid #e0e0e0',
  },
  table: {
    padding: '0.5rem',
    borderBottom: '1px solid #e0e0e0',
  },
  metrics: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
  }
};

// Add keyframes animation to document head if not already present
const addSkeletonAnimation = () => {
  const existingStyle = document.getElementById('skeleton-animation');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'skeleton-animation';
    style.textContent = `
      @keyframes skeleton-loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// Memoized skeleton components for better performance
export const SkeletonCard = memo(() => {
  React.useEffect(() => {
    addSkeletonAnimation();
  }, []);

  return (
    <div style={skeletonStyles.card}>
      {/* Card header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ ...skeletonStyles.skeleton, width: '60%', height: '1.25rem' }} />
        <div style={{ ...skeletonStyles.skeleton, width: '20%', height: '1rem' }} />
      </div>
      
      {/* Card description */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ ...skeletonStyles.skeleton, width: '100%', height: '0.875rem', marginBottom: '0.5rem' }} />
        <div style={{ ...skeletonStyles.skeleton, width: '80%', height: '0.875rem' }} />
      </div>
      
      {/* Card metrics */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ ...skeletonStyles.skeleton, width: '100%', height: '1.25rem', marginBottom: '0.25rem' }} />
            <div style={{ ...skeletonStyles.skeleton, width: '70%', height: '0.75rem' }} />
          </div>
        ))}
      </div>
    </div>
  );
});

SkeletonCard.displayName = 'SkeletonCard';

export const SkeletonList = memo<{ count?: number }>(({ count = 5 }) => {
  React.useEffect(() => {
    addSkeletonAnimation();
  }, []);

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} style={skeletonStyles.list}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ ...skeletonStyles.skeleton, width: '40px', height: '40px', borderRadius: '50%' }} />
            <div style={{ flex: 1 }}>
              <div style={{ ...skeletonStyles.skeleton, width: '70%', height: '1rem', marginBottom: '0.5rem' }} />
              <div style={{ ...skeletonStyles.skeleton, width: '50%', height: '0.875rem' }} />
            </div>
            <div style={{ ...skeletonStyles.skeleton, width: '60px', height: '0.875rem' }} />
          </div>
        </div>
      ))}
    </>
  );
});

SkeletonList.displayName = 'SkeletonList';

export const SkeletonTable = memo<{ rows?: number; columns?: number }>(({ rows = 5, columns = 4 }) => {
  React.useEffect(() => {
    addSkeletonAnimation();
  }, []);

  return (
    <div>
      {/* Table header */}
      <div style={{ ...skeletonStyles.table, backgroundColor: '#f5f5f5' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {Array.from({ length: columns }).map((_, index) => (
            <div key={index} style={{ ...skeletonStyles.skeleton, width: `${100/columns}%`, height: '1rem' }} />
          ))}
        </div>
      </div>
      
      {/* Table rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} style={skeletonStyles.table}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div key={colIndex} style={{ ...skeletonStyles.skeleton, width: `${100/columns}%`, height: '0.875rem' }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

SkeletonTable.displayName = 'SkeletonTable';

export const SkeletonMetrics = memo(() => {
  React.useEffect(() => {
    addSkeletonAnimation();
  }, []);

  return (
    <div style={skeletonStyles.metrics}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{ textAlign: 'center' }}>
          <div style={{ ...skeletonStyles.skeleton, width: '3rem', height: '2rem', marginBottom: '0.5rem' }} />
          <div style={{ ...skeletonStyles.skeleton, width: '4rem', height: '0.875rem' }} />
        </div>
      ))}
    </div>
  );
});

SkeletonMetrics.displayName = 'SkeletonMetrics';

// Main SkeletonLoader component
export const SkeletonLoader = memo<SkeletonLoaderProps>(({
  variant = 'card',
  count = 1,
  height = 'auto',
  width = '100%',
  className = ''
}) => {
  React.useEffect(() => {
    addSkeletonAnimation();
  }, []);

  const baseStyle = {
    ...skeletonStyles.skeleton,
    height,
    width,
  };

  if (variant === 'card') {
    return (
      <div className={className}>
        {Array.from({ length: count }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={className}>
        <SkeletonList count={count} />
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div className={className}>
        <SkeletonTable rows={count} />
      </div>
    );
  }

  if (variant === 'metrics') {
    return (
      <div className={className}>
        <SkeletonMetrics />
      </div>
    );
  }

  // Default skeleton
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} style={baseStyle} />
      ))}
    </div>
  );
});

SkeletonLoader.displayName = 'SkeletonLoader';

// Export SkeletonPatterns object for LoadingPatterns.tsx
export const SkeletonPatterns = {
  Dashboard: memo(() => (
    <div>
      <SkeletonMetrics />
      <div style={{ marginTop: '1rem' }}>
        <SkeletonCard />
      </div>
    </div>
  )),
  DataTable: memo<{ rows?: number }>(({ rows = 5 }) => (
    <SkeletonTable rows={rows} columns={5} />
  )),
  Form: memo<{ fields?: number }>(({ fields = 4 }) => (
    <div>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} style={{ marginBottom: '1rem' }}>
          <div style={{ ...skeletonStyles.skeleton, height: '20px', width: '100px', marginBottom: '0.5rem' }} />
          <div style={{ ...skeletonStyles.skeleton, height: '40px', width: '100%' }} />
        </div>
      ))}
    </div>
  )),
  CardGrid: memo<{ items?: number }>(({ items = 6 }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
      {Array.from({ length: items }).map((_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>
  )),
  Navigation: memo<{ items?: number }>(({ items = 5 }) => (
    <div>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} style={{ ...skeletonStyles.skeleton, height: '40px', width: '100%', marginBottom: '0.5rem' }} />
      ))}
    </div>
  )),
  Chart: memo<{ height?: number }>(({ height = 300 }) => (
    <div style={{ ...skeletonStyles.skeleton, height: `${height}px`, width: '100%' }} />
  ))
};